import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit, TrackByFunction, ViewChild } from "@angular/core";
import { TranslocoService } from "@ngneat/transloco";
import { MatDialog } from "@angular/material/dialog";
import { Compbaser } from "../compbaser";
import {
    actionAddResources,
    actionPexelsSearchPhotos,
    actionPexelsSearchPhotosReply,
    actionPexelsSearchVideos,
    actionPexelsSearchVideosReply
} from "../../store/actions/user.actions";
import {
    AppService,
    ImportService,
    LocalStoreService,
    StoreSelectorsService,
} from "../../services";
import { FileObj, IResource, IUser, UploadStatus } from "../../interfaces";
import { ToastrService } from "ngx-toastr";
import { formatBytes, TOAST_FASTER } from "../../common";
import { ofType } from "@ngrx/effects";
import { ActionsSubject } from "@ngrx/store";
import { Lib } from "../../../libs/Lib";

export interface IPexelsPhoto {
    id: number;
    width: number;
    height: number;
    url: string;
    photographer: string;
    photographer_url: string;
    photographer_id: number;
    avg_color: string;
    src: {
        original: string;
        large2x: string;
        large: string;
        medium: string;
        small: string;
        portrait: string;
        landscape: string;
        tiny: string;
    };
    liked: boolean;
    alt: string;
    downloaded_path?: string;
    downloaded_filename?: string;
    preview_url_http?: string;
    preview_url_https?: string;
    downloading?: boolean;
}

export interface IPexelsVideo {
    id: number;
    width: number;
    height: number;
    url: string;
    image: string;
    duration: number;
    user: {
        id: number;
        name: string;
        url: string;
    };
    video_files: Array<{
        id: number;
        quality: string;
        file_type: string;
        width: number;
        height: number;
        link: string;
    }>;
    video_pictures: Array<{
        id: number;
        picture: string;
        nr: number;
    }>;
    downloaded_path?: string;
    downloaded_filename?: string;
    downloaded_quality?: string;
    preview_url_http?: string;
    preview_url_https?: string;
    downloading?: boolean;
}

export interface IPexelsUsageStats {
    count: number;
    remaining: number;
    max_calls: number;
}

@Component({
    selector: "app-stock-resources",
    templateUrl: "./stock-resources.html",
    styleUrls: ["./stock-resources.scss"],
})
export class StockResources extends Compbaser implements OnInit, OnDestroy {
    @Input() miniMode = false;

    @ViewChild('videoPreviewDialog', { static: false }) videoPreviewDialog;

    user: IUser;
    searchQuery = "";
    loading = false;
    selectedMediaType: "image" | "video" = "image";

    // Results
    photos: IPexelsPhoto[] = [];
    videos: IPexelsVideo[] = [];

    // Pagination
    currentPage = 1;
    totalResults = 0;
    perPage = 80;
    hasMoreResults = false;
    loadingMore = false;

    // Video preview
    previewingVideo: IPexelsVideo | null = null;

    // Usage stats - initialize with defaults to show before first search
    photoUsage: IPexelsUsageStats = {
        count: 0,
        remaining: 10,
        max_calls: 10
    };
    videoUsage: IPexelsUsageStats = {
        count: 0,
        remaining: 10,
        max_calls: 10
    };

    constructor(
        public storeSelector: StoreSelectorsService,
        private toastr: ToastrService,
        private cd: ChangeDetectorRef,
        private appService: AppService,
        private actionsSubj: ActionsSubject,
        private translocoService: TranslocoService,
        private importService: ImportService,
        public override dialog: MatDialog,
    ) {
        super(dialog);
        this.cod(
            this.storeSelector.getUser().subscribe((i_user: IUser | null) => {
                if (i_user) {
                    this.user = i_user;
                }
            }),
        );

        // Listen for Pexels search photos reply
        this.cod(
            actionsSubj.pipe(ofType(actionPexelsSearchPhotosReply))
                .subscribe((reply: any) => {
                    this.loading = false;
                    this.loadingMore = false;
                    const payload = reply?.payload;

                    if (payload?.error) {
                        this.toastr.error(payload.error, "Search Error");
                    } else if (payload?.photos && Array.isArray(payload.photos)) {
                        // Append photos for pagination (not replace)
                        if (this.currentPage === 1) {
                            this.photos = payload.photos;
                        } else {
                            this.photos = [...this.photos, ...payload.photos];
                        }

                        if (this.photos.length === 0 && this.currentPage === 1) {
                            this.toastr.info("No photos found. Try a different search query.");
                        }

                        // Update pagination metadata
                        this.totalResults = payload.total_results || 0;
                        this.hasMoreResults = this.photos.length < this.totalResults;

                        // Extract usage stats from response
                        if (payload.usage) {
                            this.photoUsage = {
                                count: payload.usage.count,
                                remaining: payload.usage.remaining,
                                max_calls: payload.usage.max_calls
                            };
                        }
                    } else {
                        this.toastr.error("Invalid response format from server.");
                    }

                    this.cd.markForCheck();
                })
        );

        // Listen for Pexels search videos reply
        this.cod(
            actionsSubj.pipe(ofType(actionPexelsSearchVideosReply))
                .subscribe((reply: any) => {
                    this.loading = false;
                    this.loadingMore = false;
                    const payload = reply?.payload;

                    if (payload?.error) {
                        this.toastr.error(payload.error, "Search Error");
                    } else if (payload?.videos && Array.isArray(payload.videos)) {
                        // Append videos for pagination (not replace)
                        if (this.currentPage === 1) {
                            this.videos = payload.videos;
                        } else {
                            this.videos = [...this.videos, ...payload.videos];
                        }

                        if (this.videos.length === 0 && this.currentPage === 1) {
                            this.toastr.info("No videos found. Try a different search query.");
                        }

                        // Update pagination metadata
                        this.totalResults = payload.total_results || 0;
                        this.hasMoreResults = this.videos.length < this.totalResults;

                        // Extract usage stats from response
                        if (payload.usage) {
                            this.videoUsage = {
                                count: payload.usage.count,
                                remaining: payload.usage.remaining,
                                max_calls: payload.usage.max_calls
                            };
                        }
                    } else {
                        this.toastr.error("Invalid response format from server.");
                    }

                    this.cd.markForCheck();
                })
        );
    }

    ngOnInit() {
        // Initialize with default state
    }

    // Media type selection handler
    onMediaTypeSelection(e: any): void {
        const value = e.value;
        if (value === "image" || value === "video") {
            this.clearResults();
            this.selectedMediaType = value;
            this.cd.markForCheck();
        }
    }

    // Search for resources
    searchResources(): void {
        if (this.loading) return;

        if (!this.user) {
            this.toastr.error("User data not available. Please reload.", "Error");
            return;
        }

        if (Lib.IsAccessBlocked(this.user)) {
            this.toastr.error(
                this.translocoService.translate("account.screenDisruption", {}, this.translocoService.getActiveLang()),
                "[ Contact live chat ]"
            );
            return;
        }

        const sanitizedQuery = this.sanitizeQuery(this.searchQuery);
        if (!sanitizedQuery || sanitizedQuery.length < 3) {
            this.toastr.warning("Please enter at least 3 characters for the search query.");
            return;
        }

        this.clearResults();
        this.currentPage = 1;
        this.totalResults = 0;
        this.hasMoreResults = false;
        this.loading = true;
        this.cd.markForCheck();

        // Dispatch action based on media type
        if (this.selectedMediaType === "image") {
            this.storeSelector.dispatch(actionPexelsSearchPhotos({
                query: sanitizedQuery,
                perPage: this.perPage,
                page: 1,
                download: false
            }));
        } else {
            this.storeSelector.dispatch(actionPexelsSearchVideos({
                query: sanitizedQuery,
                perPage: this.perPage,
                page: 1,
                download: false,
                quality: "hd"
            }));
        }
    }

    // Load more results (pagination)
    loadMoreResults(): void {
        if (this.loadingMore || this.loading || !this.hasMoreResults) return;

        if (!this.user) {
            this.toastr.error("User data not available. Please reload.", "Error");
            return;
        }

        if (Lib.IsAccessBlocked(this.user)) {
            this.toastr.error(
                this.translocoService.translate("account.screenDisruption", {}, this.translocoService.getActiveLang()),
                "[ Contact live chat ]"
            );
            return;
        }

        const sanitizedQuery = this.sanitizeQuery(this.searchQuery);
        if (!sanitizedQuery) return;

        this.currentPage++;
        this.loadingMore = true;
        this.cd.markForCheck();

        // Dispatch action based on media type
        if (this.selectedMediaType === "image") {
            this.storeSelector.dispatch(actionPexelsSearchPhotos({
                query: sanitizedQuery,
                perPage: this.perPage,
                page: this.currentPage,
                download: false
            }));
        } else {
            this.storeSelector.dispatch(actionPexelsSearchVideos({
                query: sanitizedQuery,
                perPage: this.perPage,
                page: this.currentPage,
                download: false,
                quality: "hd"
            }));
        }
    }

    // Download photo to resources using import service pattern
    async downloadPhoto(photo: IPexelsPhoto): Promise<void> {
        if (photo.downloading) return;

        if (!this.user) {
            this.toastr.error("User data not available. Please reload.", "Error");
            return;
        }

        if (Lib.IsAccessBlocked(this.user)) {
            this.toastr.error(
                this.translocoService.translate("account.screenDisruption", {}, this.translocoService.getActiveLang()),
                "[ Contact live chat ]"
            );
            return;
        }

        photo.downloading = true;
        this.cd.markForCheck();

        try {
            this.toastr.info("Preparing photo for upload...", "", TOAST_FASTER);
            this.appService.setUploadingStatus({ process: 0, show: true });

            // Download from Pexels large image URL
            const imageUrl = photo.src.large2x || photo.src.large;
            const response = await fetch(imageUrl);
            const blob = await response.blob();

            // Create filename from photographer name and photo id
            const photographerName = photo.photographer || 'unknown';
            const sanitizedName = photographerName.toLowerCase().replace(/[^a-z0-9]/g, '_');
            const filename = `pexels_${sanitizedName}_${photo.id}.jpg`;
            const file = new File([blob], filename, { type: "image/jpeg" });

            // Use AppService upload pattern (same as resources-ai)
            const fileToUpload: FileObj = {
                fileName: filename,
                fileType: 'jpg',
                file: file,
                fileId: `${filename}-${file.lastModified || Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
                progress: 0,
                uploadStatus: UploadStatus.READY,
                formattedSize: formatBytes(file.size, 2),
                fileSize: file.size,
                skipAutoBackground: true  // Prevent auto-adding as scene background
            };

            this.appService.setUploadingFiles([fileToUpload]);
            this.appService.uploadResourceFiles();

        } catch (error) {
            console.error("Download photo error:", error);
            this.toastr.error("Failed to download photo. Please try again.");
            this.appService.setUploadingStatus({ process: 0, show: false });
        } finally {
            photo.downloading = false;
            this.cd.markForCheck();
        }
    }

    // Download video to resources using import service pattern
    async downloadVideo(video: IPexelsVideo): Promise<void> {
        if (video.downloading) return;

        if (!this.user) {
            this.toastr.error("User data not available. Please reload.", "Error");
            return;
        }

        if (Lib.IsAccessBlocked(this.user)) {
            this.toastr.error(
                this.translocoService.translate("account.screenDisruption", {}, this.translocoService.getActiveLang()),
                "[ Contact live chat ]"
            );
            return;
        }

        video.downloading = true;
        this.cd.markForCheck();

        try {
            this.toastr.info("Preparing video for upload...", "", TOAST_FASTER);
            this.appService.setUploadingStatus({ process: 0, show: true });

            // Find the best quality video file
            const videoFile = video.video_files.find(f => f.quality === 'hd') || video.video_files[0];
            if (!videoFile) {
                throw new Error("No video file available");
            }

            // Download from Pexels video URL
            const response = await fetch(videoFile.link);
            const blob = await response.blob();

            // Create filename from user name and video id
            const userName = video.user?.name || 'unknown';
            const sanitizedName = userName.toLowerCase().replace(/[^a-z0-9]/g, '_');
            const extension = videoFile.file_type.split('/')[1] || 'mp4';
            const filename = `pexels_${sanitizedName}_${video.id}.${extension}`;
            const file = new File([blob], filename, { type: videoFile.file_type });

            // Use AppService upload pattern (same as resources-ai)
            const fileToUpload: FileObj = {
                fileName: filename,
                fileType: extension,
                file: file,
                fileId: `${filename}-${file.lastModified || Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
                progress: 0,
                uploadStatus: UploadStatus.READY,
                formattedSize: formatBytes(file.size, 2),
                fileSize: file.size,
                skipAutoBackground: true  // Prevent auto-adding as scene background
            };

            this.appService.setUploadingFiles([fileToUpload]);
            this.appService.uploadResourceFiles();

        } catch (error) {
            console.error("Download video error:", error);
            this.toastr.error("Failed to download video. Please try again.");
            this.appService.setUploadingStatus({ process: 0, show: false });
        } finally {
            video.downloading = false;
            this.cd.markForCheck();
        }
    }

    // Sanitize search query
    private sanitizeQuery(text: string): string {
        if (!text) return "";
        let sanitized = text.replace(/[\r\n]+/g, " ");
        sanitized = sanitized.replace(/\s+/g, " ");
        sanitized = sanitized.trim();
        return sanitized;
    }

    // Clear search results
    clearResults(): void {
        this.photos = [];
        this.videos = [];
        this.cd.markForCheck();
    }

    // Preview video in dialog
    previewVideo(video: IPexelsVideo): void {
        this.previewingVideo = video;
        this.dialog.open(this.videoPreviewDialog, {
            width: '900px',
            maxWidth: '95vw',
            maxHeight: '95vh',
            panelClass: 'video-preview-dialog-panel',
            autoFocus: false,
            restoreFocus: false
        });
    }

    // Get video stream URL for preview
    getVideoStreamUrl(video: IPexelsVideo): string {
        if (!video) return '';
        // Find HD quality video file for streaming
        const videoFile = video.video_files.find(f => f.quality === 'hd') ||
                         video.video_files.find(f => f.quality === 'sd') ||
                         video.video_files[0];
        return videoFile ? videoFile.link : '';
    }

    // TrackBy functions for ngFor optimization
    trackByPhotoId: TrackByFunction<IPexelsPhoto> = (index: number, photo: IPexelsPhoto): number => {
        return photo.id;
    };

    trackByVideoId: TrackByFunction<IPexelsVideo> = (index: number, video: IPexelsVideo): number => {
        return video.id;
    };

    // Cleanup
    override destroy(): void {
        super.destroy();
    }

    override ngOnDestroy(): void {
        this.destroy();
    }
}
